import React, { useEffect, useState, useRef, useCallback } from 'react';
import { socket } from '../socket';
// Firestore import 제거
// import { db } from '../firebaseConfig';
// import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';

interface DispatchReportSidebarProps {
  open: boolean;
  collapsed?: boolean;
  selectedDate?: string;
  onClose: () => void;
  onToggleCollapse?: () => void;
  onSelectDate: (date: string) => void;
}

function getKoreanDayShort(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('ko-KR', { weekday: 'short' });
}

/** 사이드바 날짜(yyyy-MM-dd)와 동일한 기준 — 한국 달력 "오늘" */
function getTodayYmdSeoul(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
}

const DispatchReportSidebar: React.FC<DispatchReportSidebarProps> = ({ open, collapsed = false, selectedDate, onClose, onToggleCollapse, onSelectDate }) => {
  const [dates, setDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const datesRef = useRef<string[]>([]);
  useEffect(() => {
    datesRef.current = dates;
  }, [dates]);
  // 3일 전 ~ 앞으로 일주일(오늘+7일)까지 초기 표시
  const [dateRange, setDateRange] = useState(() => {
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    const weekAhead = new Date();
    weekAhead.setDate(weekAhead.getDate() + 7);
    return {
      start: threeDaysAgo.toISOString().split('T')[0],
      end: weekAhead.toISOString().split('T')[0]
    };
  });
  const [unassignedDates, setUnassignedDates] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleteStep, setDeleteStep] = useState<1 | 2 | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [exportedDates, setExportedDates] = useState<string[]>([]);
  const [exportCountByDate, setExportCountByDate] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!open) return;
    const fetchDates = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/dispatch-reports?start=${dateRange.start}&end=${dateRange.end}`);
        const data = await res.json();
        const dateList: string[] = data.map((item: any) => item.id).sort((a: string, b: string) => b.localeCompare(a));
        setDates(dateList);
        
        // 미배차 상태 조회 최적화 - 배치 API 사용
        const unassigned = new Set<string>();
        try {
          // 배치 API로 한 번에 조회 (가장 효율적)
          const datesParam = dateList.join(',');
          const res2 = await fetch(`/api/dispatch-reports/unassigned-count-batch?dates=${datesParam}`);
          if (res2.ok) {
            const batchResults = await res2.json();
            Object.entries(batchResults).forEach(([date, result]: [string, any]) => {
              if (result.hasUnassigned) {
                unassigned.add(date);
              }
            });
          } else {
            // 배치 API가 없으면 개별 API 사용
            await Promise.all(dateList.map(async (date) => {
              try {
                const res3 = await fetch(`/api/dispatch-reports/${date}/unassigned-count`);
                if (res3.ok) {
                  const { hasUnassigned } = await res3.json();
                  if (hasUnassigned) {
                    unassigned.add(date);
                  }
                } else {
                  // 개별 API도 없으면 기존 방식으로 폴백 (창고료 예외 동일 적용)
                  const res4 = await fetch(`/api/dispatch-reports/${date}/rows`);
                  const rows = await res4.json();
                  const hasUnassigned = rows.some((row: any) => {
                    const hasRoute = row.route && String(row.route).trim() !== '';
                    const hasNoVehicle = !row.vehicleNumber || String(row.vehicleNumber).trim() === '';
                    const hasWarehouseFee = row.route && String(row.route).includes('창고료');
                    return hasRoute && hasNoVehicle && !hasWarehouseFee;
                  });
                  if (hasUnassigned) unassigned.add(date);
                }
              } catch (error) {
                console.error(`미배차 상태 조회 실패 (${date}):`, error);
              }
            }));
          }
        } catch (error) {
          console.error('배치 미배차 상태 조회 실패:', error);
          // 배치 실패시 개별 조회로 폴백
          await Promise.all(dateList.map(async (date) => {
            try {
              const res3 = await fetch(`/api/dispatch-reports/${date}/unassigned-count`);
              if (res3.ok) {
                const { hasUnassigned } = await res3.json();
                if (hasUnassigned) {
                  unassigned.add(date);
                }
              }
            } catch (error) {
              console.error(`미배차 상태 조회 실패 (${date}):`, error);
            }
          }));
        }
        
        setUnassignedDates(unassigned);
      } catch (error) {
        console.error('날짜 목록 로드 실패:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDates();
    // 배차일보 수정 시에는 날짜 목록은 건드리지 않고 미배차 수만 갱신 (전체 fetchDates 호출 시 속도 저하 방지)
    const refreshUnassignedOnly = async () => {
      const dateList = datesRef.current;
      if (dateList.length === 0) return;
      try {
        const datesParam = dateList.join(',');
        const res2 = await fetch(`/api/dispatch-reports/unassigned-count-batch?dates=${datesParam}`);
        if (!res2.ok) return;
        const batchResults = await res2.json();
        const unassigned = new Set<string>();
        Object.entries(batchResults).forEach(([date, result]: [string, any]) => {
          if (result.hasUnassigned) unassigned.add(date);
        });
        setUnassignedDates(unassigned);
      } catch {
        // 배치 실패 시 무시 (날짜 목록/로딩 상태는 변경하지 않음)
      }
    };
    const handler = () => refreshUnassignedOnly();
    window.addEventListener('refresh-unassigned-dates', handler);
    return () => {
      window.removeEventListener('refresh-unassigned-dates', handler);
    };
  }, [open, dateRange]);

  // PostgreSQL에 이미 내보낸 날짜 목록 조회 (다음날 출근해도 버튼 비활성화 유지)
  const fetchExportedDates = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return;
    try {
      const res = await fetch(
        `/api/settlement-reports/pg/exported-dates?start=${dateRange.start}&end=${dateRange.end}`
      );
      if (!res.ok) return;
      const list = await res.json();
      setExportedDates(Array.isArray(list) ? list : []);
    } catch {
      setExportedDates([]);
    }
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    if (!open) return;
    fetchExportedDates();
  }, [open, collapsed, fetchExportedDates]);

  useEffect(() => {
    if (!open) return;
    const handler = (data: { source?: string; operationType?: string }) => {
      if (data?.source === 'settlement' && data?.operationType === 'row_deleted') {
        void fetchExportedDates();
      }
    };
    socket.on('report-updated', handler);
    return () => {
      socket.off('report-updated', handler);
    };
  }, [open, fetchExportedDates]);

  // 날짜 선택 변경 시 해당 날짜에 정산일보 데이터가 실제 존재하는지 확인
  useEffect(() => {
    setExportMessage(null);
    if (!open || !selectedDate) return;
    const checkDate = async () => {
      try {
        const res = await fetch(
          `/api/settlement-reports/pg/exported-dates?start=${selectedDate}&end=${selectedDate}`
        );
        if (!res.ok) return;
        const list = await res.json();
        const exists = Array.isArray(list) && list.includes(selectedDate);
        setExportedDates(prev => {
          if (exists && !prev.includes(selectedDate)) return [...prev, selectedDate];
          if (!exists && prev.includes(selectedDate)) return prev.filter(d => d !== selectedDate);
          return prev;
        });
      } catch {}
    };
    checkDate();
  }, [selectedDate, open]);

  useEffect(() => {
    if (!document.getElementById('blink-sidebar-date-style')) {
      const style = document.createElement('style');
      style.id = 'blink-sidebar-date-style';
      style.innerHTML = `
        @keyframes blinkSidebarDate { 
          0% { opacity: 1; background: #fff3e0; }
          50% { opacity: 0.88; background: #ffecb3; }
          100% { opacity: 1; background: #fff3e0; }
        }
        .blink-sidebar-date { 
          animation: blinkSidebarDate 6s ease-in-out infinite; 
          color: #d32f2f !important; 
          font-weight: bold; 
          border: 2px solid #ff9800 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // 삭제 API 호출 함수
  const handleDeleteDate = async (date: string) => {
    try {
      const res = await fetch(`/api/dispatch-reports/${date}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('삭제 실패');
      setDeleteTarget(null);
      setDeleteStep(null);
      // 삭제 후 목록 새로고침
      const res2 = await fetch(`/api/dispatch-reports?start=${dateRange.start}&end=${dateRange.end}`);
      const data = await res2.json();
      const dateList: string[] = data.map((item: any) => item.id).sort((a: string, b: string) => b.localeCompare(a));
      setDates(dateList);
    } catch (e) {
      alert('삭제 중 오류가 발생했습니다.');
      setDeleteTarget(null);
      setDeleteStep(null);
    }
  };

  const handleExportToSettlement = async () => {
    if (!selectedDate) {
      setExportMessage({ type: 'error', text: '내보낼 배차일보 날짜를 먼저 선택하세요.' });
      return;
    }
    if (!window.confirm('지금 배차일보 데이터를 정산일보로 보내시겠습니까?')) return;

    setExportMessage(null);
    setExportLoading(true);
    try {
      const res = await fetch('/api/settlement-reports/export-from-dispatch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start: selectedDate, end: selectedDate })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setExportMessage({ type: 'error', text: data?.message || data?.error || '내보내기 실패' });
        return;
      }
      const count = data?.exported ?? data?.upsertedCount ?? data?.count ?? 0;
      setExportCountByDate(prev => ({ ...prev, [selectedDate]: count }));
      setExportedDates(prev => (prev.includes(selectedDate) ? prev : [...prev, selectedDate]));
      setExportMessage(null);
    } catch (e) {
      setExportMessage({ type: 'error', text: '요청 중 오류가 발생했습니다.' });
    } finally {
      setExportLoading(false);
    }
  };

  const todayYmdSeoul = getTodayYmdSeoul();

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: collapsed ? 60 : 0,
      right: 0,
      width: collapsed ? 50 : 230,
      height: collapsed ? 'calc(100vh - 60px)' : '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e3e8ef 100%)',
      boxShadow: '-4px 0 16px rgba(0,0,0,0.10)',
      zIndex: 1000,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      borderTopLeftRadius: collapsed ? 8 : 16,
      borderBottomLeftRadius: 16,
      overflow: 'hidden',
      transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out, top 0.3s ease-in-out, border-top-left-radius 0.3s ease-in-out'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: collapsed ? '24px 8px 12px 8px' : '24px 18px 12px 18px',
        borderBottom: '1px solid #e0e6ed',
        background: 'rgba(255,255,255,0.95)'
      }}>
        {!collapsed && <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: '#222' }}>배차일보</h3>}
        <div style={{ display: 'flex', gap: 4 }}>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 14,
                color: '#666',
                cursor: 'pointer',
                borderRadius: '50%',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s, color 0.2s'
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#f0f0f0')}
              onMouseOut={e => (e.currentTarget.style.background = 'none')}
              title={collapsed ? "펼치기" : "접기"}
            >
              {collapsed ? "◀" : "▶"}
            </button>
          )}
          <button
            onClick={() => { /* console.log('[사이드바] 닫기 버튼 클릭 또는 onClose 호출'); */ onClose(); }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 15,
              color: '#888',
              cursor: 'pointer',
              borderRadius: '50%',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s, color 0.2s'
            }}
            onMouseOver={e => (e.currentTarget.style.background = '#f0f0f0')}
            onMouseOut={e => (e.currentTarget.style.background = 'none')}
            title="닫기"
          >×</button>
        </div>
      </div>
      <div style={{ 
        padding: collapsed ? '16px 8px 0 8px' : '16px 14px 0 14px', 
        flex: 1, 
        overflowY: 'auto', 
        display: 'flex', 
        flexDirection: 'column',
        opacity: collapsed ? 0 : 1,
        transition: 'opacity 0.2s ease-in-out'
      }}>
        {/* 접힌 상태에서 날짜 선택 드롭다운 */}
        {collapsed && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 8, 
            padding: '8px 4px'
          }}>
            <select
              value={selectedDate || ''}
              onChange={(e) => onSelectDate(e.target.value)}
              style={{
                padding: '4px 6px',
                border: '1px solid #ddd',
                borderRadius: 4,
                fontSize: 12,
                background: '#fff',
                cursor: 'pointer'
              }}
              title="날짜 선택"
            >
              <option value="">날짜 선택</option>
              {dates.map(date => (
                <option key={date} value={date}>
                  {date} ({getKoreanDayShort(date)})
                </option>
              ))}
            </select>
          </div>
        )}
        {!collapsed && (
          <>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 6, 
              marginBottom: 18, 
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              padding: '10px 8px'
            }}>
              <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 2, fontSize: 14 }}>기간 선택</div>
              <input 
                type="date" 
                value={dateRange.start} 
                onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}
                style={{
                  padding: '4px 6px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 13,
                  marginBottom: 4
                }}
              />
              <input 
                type="date" 
                value={dateRange.end} 
                onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}
                style={{
                  padding: '4px 6px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 13
                }}
              />
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={handleExportToSettlement}
                  disabled={exportLoading || !selectedDate || (selectedDate != null && exportedDates.includes(selectedDate))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    cursor: exportLoading || !selectedDate || (selectedDate != null && exportedDates.includes(selectedDate)) ? 'not-allowed' : 'pointer',
                    opacity: selectedDate != null && exportedDates.includes(selectedDate) ? 0.8 : 1
                  }}
                >
                  {exportLoading
                    ? '내보내는 중…'
                    : selectedDate != null && exportedDates.includes(selectedDate)
                      ? '내보내기 완료 (해당 날짜)'
                      : '데이터 내보내기 (정산용)'}
                </button>
                {(() => {
                  const showSuccessForDate = selectedDate && exportedDates.includes(selectedDate) && exportCountByDate[selectedDate] != null;
                  const showError = exportMessage?.type === 'error';
                  if (!showSuccessForDate && !showError) return null;
                  return (
                    <p
                      style={{
                        marginTop: 6,
                        marginBottom: 0,
                        fontSize: 12,
                        color: showError ? '#c62828' : '#2e7d32'
                      }}
                    >
                      {showError ? exportMessage!.text : `내보내기 완료 (${exportCountByDate[selectedDate!]}건)`}
                    </p>
                  );
                })()}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingLeft: 4, paddingRight: 2, boxSizing: 'border-box' }}>
              {loading ? (
                <div style={{ color: '#888', textAlign: 'center', marginTop: 40 }}>로딩 중...</div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {dates.map(date => {
                    const isToday = date === todayYmdSeoul;
                    const dateButton = (
                      <button
                        type="button"
                        onClick={() => onSelectDate(date)}
                        className={unassignedDates.has(date) ? 'blink-sidebar-date' : ''}
                        style={{
                          width: '100%',
                          padding: '10px 0',
                          border: 'none',
                          borderRadius: 8,
                          background: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 600,
                          fontSize: 15,
                          cursor: 'pointer',
                          boxShadow: '0 1px 4px rgba(30,60,90,0.04)',
                          transition: 'background 0.2s, color 0.2s',
                          textAlign: 'center',
                          boxSizing: 'border-box'
                        }}
                        onMouseOver={e => {
                          e.currentTarget.style.background = '#1976d2';
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.background = '#e3f2fd';
                          e.currentTarget.style.color = '#1976d2';
                        }}
                      >
                        {/* 날짜와 요일 분리, 요일별 색상 적용 */}
                        {(() => {
                          const dayKor = getKoreanDayShort(date);
                          const dayColorMap: { [key: string]: string } = {
                            '월': '#ff9800', // 주황
                            '화': '#388e3c', // 초록
                            '수': '#00bcd4', // 청록
                            '목': '#7b1fa2', // 보라
                            '금': '#d32f2f', // 빨강
                            '토': '#795548', // 갈색
                            '일': '#f44336' // 진빨강
                          };
                          return (
                            <>
                              {date} (
                              <span style={{ color: dayColorMap[dayKor] || '#1976d2', fontWeight: 700 }}>{dayKor}</span>)
                            </>
                          );
                        })()}
                      </button>
                    );
                    return (
                <li key={date} style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {isToday ? (
                    <div
                      title="오늘 날짜"
                      style={{
                        width: 'calc(100% - 28px)',
                        padding: 3,
                        borderRadius: 11,
                        background: '#0d47a1',
                        boxSizing: 'border-box',
                        flexShrink: 0
                      }}
                    >
                      {dateButton}
                    </div>
                  ) : (
                    <div style={{ width: 'calc(100% - 28px)', flexShrink: 0 }}>{dateButton}</div>
                  )}
                  <button
                    type="button"
                    onClick={() => { setDeleteTarget(date); setDeleteStep(1); }}
                    style={{
                      marginLeft: 4,
                      background: 'none',
                      border: 'none',
                      color: '#d32f2f',
                      fontSize: 13,
                      cursor: 'pointer',
                      width: 13,
                      height: 13,
                      minWidth: 13,
                      minHeight: 13,
                      padding: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s'
                    }}
                    title="배차일보 삭제"
                  >
                    ❌
                  </button>
                </li>
                    );
                  })}
            </ul>
          )}
        </div>
        </>
        )}
      </div>
      {/* 삭제 2단계 경고 모달 */}
      {deleteTarget && deleteStep === 1 && (
        <div style={{
          position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 20000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, minWidth: 320, boxShadow: '0 2px 16px rgba(0,0,0,0.15)' }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 18, color: '#d32f2f' }}>
              배차일보를 삭제하면 해당 날짜의 모든 배차내용이 모두 삭제됩니다.<br/>계속 진행하시겠습니까?
            </div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => { setDeleteStep(null); setDeleteTarget(null); }} style={{ background: '#eee', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 500, cursor: 'pointer' }}>아니오</button>
              <button onClick={() => setDeleteStep(2)} style={{ background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}>예</button>
            </div>
          </div>
        </div>
      )}
      {deleteTarget && deleteStep === 2 && (
        <div style={{
          position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 20000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, minWidth: 320, boxShadow: '0 2px 16px rgba(0,0,0,0.15)' }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 18, color: '#d32f2f' }}>
              정말로 삭제하시겠습니까?<br/>선택한 날짜의 모든 배차내역이 삭제되며 복구가 불가능합니다.
            </div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => { setDeleteStep(null); setDeleteTarget(null); }} style={{ background: '#eee', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 500, cursor: 'pointer' }}>아니오</button>
              <button onClick={() => handleDeleteDate(deleteTarget)} style={{ background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}>예</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchReportSidebar; 